import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { createLogger } from './lib/debug';
import { AppShell } from './components/layout/AppShell';
import { PlanPage } from './modules/plan/PlanPage';
import { SubmissionExplorerPage } from './modules/see/SubmissionExplorerPage';
import { SubmissionViewerPage } from './modules/see/SubmissionViewerPage';
import { ReviewSummaryPage } from './modules/review/ReviewSummaryPage';
import { AbbreviationsPage } from './modules/review/AbbreviationsPage';
import { CrossReferencesPage } from './modules/review/CrossReferencesPage';
import { DoseUnitPage } from './modules/review/DoseUnitPage';
import { RenameConsistencyPage } from './modules/review/RenameConsistencyPage';
import { SponsorConsistencyPage } from './modules/review/SponsorConsistencyPage';
import { StudyProfilePage } from './modules/review/StudyProfilePage';
import { SubstanceProfilePage } from './modules/review/SubstanceProfilePage';
import { ManageProjectPage } from './modules/settings/ManageProjectPage';
import { ManageConflictsPage } from './modules/settings/ManageConflictsPage';
import { ManageAppPage } from './modules/settings/ManageAppPage';
import { ManageUsersPage } from './modules/settings/ManageUsersPage';
import { AuditReportPage } from './modules/settings/AuditReportPage';
import { AccountPage } from './modules/settings/AccountPage';
import { DocumentReadinessPage } from './modules/plan/DocumentReadinessPage';
import { ClearPage } from './modules/clear/ClearPage';
import { HelpPage } from './modules/help/HelpPage';
import { ReadinessPage } from './modules/readiness/ReadinessPage';
import { FrePage } from './modules/fre/FrePage';
import { LoginPage } from './modules/auth/LoginPage';
// SetupPage removed — Supabase Auth handles registration, first user auto-provisioned as admin
import { OAuthCallbackPage } from './modules/auth/OAuthCallbackPage';
import { Globe, Lock } from 'lucide-react';
import { ProjectProvider, useProject } from './lib/ProjectContext';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { DevModeProvider, useDevMode } from './lib/DevModeContext';
import { DashboardCacheProvider } from './lib/DashboardCacheContext';
import { SubmissionFocusProvider } from './lib/SubmissionFocusContext';
import { ScanStatusProvider } from './lib/ScanStatusContext';
import { ActiveOrgProvider, useActiveOrg } from './lib/ActiveOrgContext';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { EnvironmentBanner, showEnvBanner } from './components/EnvironmentBanner';
import { Telemetry, setCurrentScreen, setTelemetryContext, getSessionDurationSeconds } from './lib/telemetry';

const appLog = createLogger('app');

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading, authRequired } = useAuth();
  const location = useLocation();

  // OAuth callback must bypass the auth gate — user isn't authenticated yet
  if (location.pathname === '/auth/callback') {
    return <OAuthCallbackPage />;
  }

  if (loading) {
    appLog.log('AuthGate: loading=true, showing spinner');
    return <LoadingSpinner message="Loading..." />;
  }

  appLog.log('AuthGate: loading=false, authRequired=', authRequired, 'user=', user?.email ?? 'null');

  // If auth is configured but no user is logged in, show login
  if (authRequired && !user) {
    return <LoginPage />;
  }

  // A2F-520 Layer 1: commons-only users (every active membership is the
  // public-signup org) don't belong in the main app — bounce them to
  // commons. Guard order matters: orgs.length > 0 blocks the synthetic
  // local-dev admin whose orgs=[] would otherwise vacuous-truth into a
  // redirect. VITE_COMMONS_URL unset silently skips the redirect so a
  // misconfigured deploy falls back to an empty main app rather than
  // a broken URL. localStorage is origin-scoped, so the user re-auths
  // on commons — acceptable: they're now at least on the right app.
  const commonsUrl = import.meta.env.VITE_COMMONS_URL;
  if (
    commonsUrl &&
    user &&
    user.orgs &&
    user.orgs.length > 0 &&
    user.orgs.every((o) => o.isPublicSignup)
  ) {
    appLog.log('AuthGate: commons-only user, redirecting to', commonsUrl);
    window.location.replace(commonsUrl);
    return <LoadingSpinner message="Redirecting…" />;
  }

  return <>{children}</>;
}

/** Route guard — redirects to home when dev mode is off. */
function GatedRoute({ children }: { children: React.ReactNode }) {
  const { devMode } = useDevMode();
  if (!devMode) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function ProjectPickerScreen() {
  const { projects, setActiveProject } = useProject();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/chrona-logo.png" alt="Chrona" className="w-16 h-16 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-brand-charcoal">Select an IND</h1>
          <p className="text-sm text-slate-500 mt-1">Choose which IND to work with</p>
        </div>
        <div className="space-y-2">
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => setActiveProject(p)}
              className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-brand-orange hover:bg-orange-50/30 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-brand-charcoal">{p.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">IND {p.indNumber}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">{p.submissionCount} submissions</p>
                  <p className="text-xs text-slate-400">{p.documentCount} documents</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Full-page org picker shown on first login for multi-org users (A2F-517).
 * Mirrors ProjectPickerScreen. Clicking an org triggers setActiveOrgId, which
 * writes localStorage and reloads; after reload the picker is skipped.
 */
function OrgPickerScreen() {
  const { orgs, setActiveOrgId } = useActiveOrg();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/chrona-logo.png" alt="Chrona" className="w-16 h-16 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-brand-charcoal">Select an organization</h1>
          <p className="text-sm text-slate-500 mt-1">Choose which org to work in</p>
        </div>
        <div className="space-y-2">
          {orgs.map((o) => (
            <button
              key={o.orgId}
              onClick={() => setActiveOrgId(o.orgId)}
              className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 transition-colors hover:border-brand-orange hover:bg-orange-50/30"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-brand-charcoal flex items-center gap-1.5">
                    <span className="truncate">{o.name}</span>
                    {o.isPublicSignup && (
                      <Globe size={12} className="text-slate-400 shrink-0" aria-label="Public archive" />
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5 capitalize">{o.role}</p>
                </div>
                {o.requiresSso && (
                  <span title="Requires Microsoft sign-in" className="inline-flex">
                    <Lock size={14} className="text-slate-400 shrink-0" />
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Gate that renders the full-page org picker before the project picker
 * when a multi-org user has no durable selection in localStorage yet.
 * Safe against races: ActiveOrgProvider derives orgs synchronously from
 * user.orgs, and AuthGate blocks rendering on auth loading.
 */
function OrgPickerGate({ children }: { children: React.ReactNode }) {
  const { orgs, hasStoredSelection } = useActiveOrg();
  if (orgs.length > 1 && !hasStoredSelection) return <OrgPickerScreen />;
  return <>{children}</>;
}

/** Syncs auth/project/devmode state to the telemetry module on every render. */
function TelemetrySync() {
  const { user } = useAuth();
  const { activeProject } = useProject();
  const { devMode } = useDevMode();

  useEffect(() => {
    setTelemetryContext({
      userId: user?.id,
      userRole: user?.role,
      projectId: activeProject?.id,
      orgId: user?.activeOrgId ?? user?.orgId ?? undefined,
      devMode,
    });
  }, [user?.id, user?.role, user?.activeOrgId, user?.orgId, activeProject?.id, devMode]);

  return null;
}

/** Tracks page views and updates the current screen name in the telemetry module. */
function TelemetryPageTracker() {
  const location = useLocation();

  useEffect(() => {
    setCurrentScreen(location.pathname);
    Telemetry.pageViewed({ page_name: location.pathname, route: location.pathname });
  }, [location.pathname]);

  return null;
}

/** Fires session_started on mount and session_ended on unload. */
function SessionLifecycle() {
  useEffect(() => {
    Telemetry.sessionStarted();

    const handleBeforeUnload = () => {
      Telemetry.sessionEnded({ session_duration_seconds: getSessionDurationSeconds() });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      Telemetry.sessionEnded({ session_duration_seconds: getSessionDurationSeconds() });
    };
  }, []);

  return null;
}

function NoProjectsMessage() {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 p-6 text-center">
        <img src="/chrona-logo.png" alt="Chrona" className="w-14 h-14 mx-auto mb-3" />
        <h1 className="text-lg font-bold text-brand-charcoal">No projects available</h1>
        <p className="text-sm text-slate-500 mt-2">
          Your administrator hasn't set up any projects yet. Contact them to get started.
        </p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { projects, loading, needsProjectSelection } = useProject();
  const { user } = useAuth();

  if (loading) {
    appLog.log('AppRoutes: project loading=true, showing spinner');
    return <LoadingSpinner message="Loading..." />;
  }

  appLog.log('AppRoutes: projects=', projects.length, 'needsProjectSelection=', needsProjectSelection);

  // A2F-520 Layer 2: FRE is an admin setup flow. Match the backend
  // POST /api/v1/projects gate (require_role("lead")) — admin and lead
  // see the FRE; everyone else sees a "contact your admin" message.
  const canSetup = user?.role === 'admin' || user?.role === 'lead';

  // Show FRE if no projects exist (admin/lead only)
  if (projects.length === 0) {
    return canSetup ? <FrePage /> : <NoProjectsMessage />;
  }

  // Multiple projects but none selected — show picker
  if (needsProjectSelection) {
    return <ProjectPickerScreen />;
  }

  return (
    <AppShell>
      <TelemetryPageTracker />
      <Routes>
        {/* A2F-1262: the Dashboard is retired — the Review Summary is the
            default project landing. Redirect so deep links / the add-in
            "Open in Chrona" target (A2F-1111) and bookmarks of `/` resolve to
            the canonical Summary URL (and the nav highlights it). */}
        <Route path="/" element={<Navigate to="/review/summary" replace />} />

        {/* See */}
        <Route path="/see/explorer" element={<SubmissionExplorerPage />} />
        <Route path="/see/viewer" element={<SubmissionViewerPage />} />
        <Route path="/see/viewer/:documentId" element={<SubmissionViewerPage />} />

        {/* Review */}
        {/* A2F-1262: the Review section index — vertical feature summary,
            promoted from the old Dashboard "Chrona Review" card. */}
        <Route path="/review/summary" element={<ReviewSummaryPage />} />
        {/* A2F-1361/1380/1445: un-gated — corpus-verified against real INDs. */}
        <Route path="/review/study-profile" element={<StudyProfilePage />} />
        {/* A2F-1363/A2F-1361: dev-only until the Substance Profile is corpus-verified. */}
        <Route
          path="/review/substance-profile"
          element={<GatedRoute><SubstanceProfilePage /></GatedRoute>}
        />
        <Route path="/review/abbreviations" element={<AbbreviationsPage />} />
        {/* A2F-1176: Compound Code removed as a standalone Review feature. */}
        <Route path="/review/cross-references" element={<CrossReferencesPage />} />
        <Route path="/review/dose-unit" element={<DoseUnitPage />} />
        {/* A2F-1445: Population retired as a standalone Review feature. */}
        <Route path="/review/rename-consistency" element={<RenameConsistencyPage />} />
        {/* A2F-1158: dev-only until the cover-letter extraction is corpus-verified. */}
        <Route
          path="/review/sponsor-consistency"
          element={<GatedRoute><SponsorConsistencyPage /></GatedRoute>}
        />
        {/* Clear — legacy redirect to Clearance (A2F-998). */}
        <Route path="/clear" element={<Navigate to="/documents/clearance" replace />} />

        {/* Plan */}
        <Route path="/plan" element={<PlanPage />} />
        <Route path="/plan/sections" element={<PlanPage />} />
        {/* Legacy Plan tabs — now live under /documents (A2F-998). */}
        <Route path="/plan/document-readiness" element={<Navigate to="/documents/signoff" replace />} />
        <Route path="/plan/document-clear" element={<Navigate to="/documents/clearance" replace />} />

        {/* Documents (A2F-998) */}
        <Route path="/documents/signoff" element={<DocumentReadinessPage />} />
        <Route path="/documents/clearance" element={<ClearPage />} />

        {/* Compliance (A2F-998) */}
        <Route path="/compliance/readiness-snapshots" element={<ReadinessPage />} />
        <Route path="/compliance/audit-report" element={<AuditReportPage />} />

        {/* Resolve — legacy redirects to Plan */}
        <Route path="/resolve/classify" element={<Navigate to="/plan/sections" replace />} />
        <Route path="/resolve" element={<Navigate to="/plan/sections" replace />} />

        {/* Help */}
        <Route path="/help" element={<HelpPage />} />

        {/* Account (A2F-600) — view-only identity + memberships */}
        <Route path="/account" element={<AccountPage />} />

        {/* Settings */}
        <Route path="/settings/project" element={<ManageProjectPage />} />
        <Route path="/settings/conflicts" element={<ManageConflictsPage />} />
        <Route path="/settings/app" element={<ManageAppPage />} />
        <Route path="/settings/users" element={<ManageUsersPage />} />
        {/* Legacy Settings routes — now live under /compliance (A2F-998). */}
        <Route path="/settings/readiness" element={<Navigate to="/compliance/readiness-snapshots" replace />} />
        <Route path="/settings/audit-report" element={<Navigate to="/compliance/audit-report" replace />} />

        {/* Legacy redirects */}
        <Route path="/readiness" element={<Navigate to="/compliance/readiness-snapshots" replace />} />

        {/* Add new IND — admin/lead only (A2F-520 Layer 2) */}
        <Route path="/fre" element={canSetup ? <FrePage /> : <Navigate to="/" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

export default function App() {
  return (
    <>
      <EnvironmentBanner />
      <div style={showEnvBanner ? { paddingTop: '32px' } : undefined}>
        <AuthProvider>
          <AuthGate>
            <ActiveOrgProvider>
              <OrgPickerGate>
                <ProjectProvider>
                  <SubmissionFocusProvider>
                  <ScanStatusProvider>
                  <DashboardCacheProvider>
                    <DevModeProvider>
                      <TelemetrySync />
                      <SessionLifecycle />
                      <AppRoutes />
                    </DevModeProvider>
                  </DashboardCacheProvider>
                  </ScanStatusProvider>
                  </SubmissionFocusProvider>
                </ProjectProvider>
              </OrgPickerGate>
            </ActiveOrgProvider>
          </AuthGate>
        </AuthProvider>
      </div>
    </>
  );
}
